import { useState } from "react";
import { MapPin, X, Search, Loader2 } from "lucide-react";

/**
 * Manual location picker overlay — shown when GPS is denied or unavailable.
 *
 * Presents a small country/city dropdown so a visitor can center the map on
 * their location without waiting for a GPS fix. Never a "world map" state.
 *
 * Countries here are a curated shortlist covering Drivon's Preview seed
 * countries + a few high-traffic capitals. Users can also free-type any
 * city name and hit Enter — we geocode via Nominatim (OpenStreetMap public
 * endpoint, ≤1 req/sec per user; safe for very light manual use).
 */
const PRESETS = [
  { country: "TR", city: "Istanbul",   lat: 41.0082, lng: 28.9784 },
  { country: "TR", city: "Ankara",     lat: 39.9334, lng: 32.8597 },
  { country: "IL", city: "Tel Aviv",   lat: 32.0853, lng: 34.7818 },
  { country: "IL", city: "Jerusalem",  lat: 31.7683, lng: 35.2137 },
  { country: "IL", city: "Haifa",      lat: 32.7940, lng: 34.9896 },
  { country: "US", city: "New York",   lat: 40.7128, lng: -74.006 },
  { country: "US", city: "Los Angeles",lat: 34.0522, lng: -118.2437 },
  { country: "GB", city: "London",     lat: 51.5074, lng: -0.1278 },
  { country: "FR", city: "Paris",      lat: 48.8566, lng: 2.3522 },
  { country: "DE", city: "Berlin",     lat: 52.5200, lng: 13.4050 },
  { country: "NL", city: "Amsterdam",  lat: 52.3676, lng: 4.9041 },
  { country: "AE", city: "Dubai",      lat: 25.2048, lng: 55.2708 },
  { country: "SA", city: "Riyadh",     lat: 24.7136, lng: 46.6753 },
  { country: "CA", city: "Toronto",    lat: 43.6532, lng: -79.3832 },
  { country: "RU", city: "Moscow",     lat: 55.7558, lng: 37.6173 },
];

export default function ManualLocationPicker({ onPick, onClose }) {
  const [query, setQuery] = useState("");
  const [geocoding, setGeocoding] = useState(false);

  const pickPreset = (p) => {
    onPick([p.lat, p.lng], { country: p.country, city: p.city });
  };

  // Free-text search via OSM Nominatim (public, no key). Rate-limited to
  // 1 req/sec; we throttle at the UI level (one manual click per submit).
  const submit = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setGeocoding(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1&addressdetails=1`;
      const r = await fetch(url, { headers: { "Accept": "application/json" } });
      const data = await r.json();
      if (Array.isArray(data) && data.length > 0) {
        const hit = data[0];
        const lat = parseFloat(hit.lat);
        const lng = parseFloat(hit.lon);
        const country = (hit.address?.country_code || "").toUpperCase();
        const city = hit.address?.city || hit.address?.town || hit.address?.village || hit.display_name.split(",")[0];
        onPick([lat, lng], { country, city });
      } else {
        alert("No matching location found. Try a different query.");
      }
    } catch {
      alert("Search failed. Try picking from the list.");
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center p-4 bg-ink-bg/85 backdrop-blur"
      data-testid="manual-location-picker"
    >
      <div className="bg-ink-surface border border-ink-border rounded-2xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="border-b border-ink-border px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-emerald-400" />
            <div>
              <div className="text-base font-black">Pick a location</div>
              <div className="text-[11px] text-white/50">Location access is off — choose where you are.</div>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10" data-testid="manual-loc-close">
              <X size={18} />
            </button>
          )}
        </div>

        <form onSubmit={submit} className="px-5 py-4 border-b border-ink-border">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search any city or address…"
              className="w-full bg-ink-surface2 border border-ink-border rounded-full ps-10 pe-4 py-2.5 text-sm"
              data-testid="manual-loc-search-input"
            />
          </div>
          <button
            type="submit"
            disabled={geocoding || !query.trim()}
            className="mt-2 w-full rounded-lg bg-white text-black py-2 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="manual-loc-search-submit"
          >
            {geocoding && <Loader2 size={14} className="animate-spin" />}
            {geocoding ? "Searching…" : "Search & center map"}
          </button>
        </form>

        <div className="overflow-y-auto flex-1 px-5 py-3">
          <div className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-2">Popular</div>
          <div className="space-y-1">
            {PRESETS.map((p, i) => (
              <button
                key={i}
                onClick={() => pickPreset(p)}
                className="w-full text-start px-3 py-2.5 rounded-lg hover:bg-white/5 flex items-center gap-3 border border-transparent hover:border-white/10"
                data-testid={`manual-loc-preset-${p.country}-${p.city.replace(/\s/g, "").toLowerCase()}`}
              >
                <span className="w-8 h-8 rounded-full bg-ink-surface2 flex items-center justify-center text-xs font-black">{p.country}</span>
                <div className="flex-1">
                  <div className="text-sm font-bold">{p.city}</div>
                  <div className="text-[11px] text-white/50">{p.lat.toFixed(2)}, {p.lng.toFixed(2)}</div>
                </div>
                <MapPin size={14} className="text-white/40" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
